import { guardRoute, type AuthDeps } from '@esnafai/shared/auth';
import { createRootRouteWithContext, Outlet, useMatches } from '@tanstack/react-router';
import { NotFoundState } from '@/components/NotFoundState/NotFoundState';
import { MerchantShell } from '@/components/shell/MerchantShell';
import { t } from '@esnafai/shared/i18n';

// Every navigation passes the guard (UI architecture §6): public routes are open, the rest need a session, unfinished
// onboarding goes to its step, and a role outside a route's roles renders the inline role state in place (roleGuarded).
export const Route = createRootRouteWithContext<AuthDeps>()({
  beforeLoad: ({ context, location, matches }) =>
    guardRoute({
      ...context,
      location,
      staticData: matches.map((match) => match.staticData),
    }),
  component: Root,
  notFoundComponent: () => (
    <div className="p-4">
      <NotFoundState
        message={t('merchant.shell.notFound.message')}
        backTo="/"
        backLabel={t('merchant.shell.notFound.back')}
      />
    </div>
  ),
});

// The public pages and the onboarding wizard have no navigation (merchant screens §3.1); every other route sits in
// the shell.
function Root() {
  const matches = useMatches();
  const bare =
    matches.some((match) => match.staticData?.public) ||
    matches.some((match) => match.pathname.startsWith('/onboarding'));
  // A forbidden route renders the role state itself (roleGuarded), under its area's layout.
  return bare ? (
    <Outlet />
  ) : (
    <MerchantShell>
      <Outlet />
    </MerchantShell>
  );
}
